/**
 * V2 Paywall Body Section Configuration
 *
 * V2 version of paywallBodySectionConfig.ts with V2 data paths.
 * Title/subtitle in content.copy, feature bullets in content.options.
 *
 * V2 path mapping:
 * - V1 title → V2 content.copy.title
 * - V1 subtitle → V2 content.copy.subtitle
 * - V1 list.items → V2 content.options.items
 * - V1 l10n → V2 translations
 * - V1 styles → V2 appearance
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import type { FieldConfig } from '../types/editorFieldTypes';
import {
  createTextStyleFields,
  createSimpleImageStyleFields,
  createListItemStateFields,
  V2_TITLE_TEXT_STYLE_FIELDS,
  V2_TEXT_ALIGN_OPTIONS,
} from './atomicFields';

export interface BulletStyleSectionConfig {
  id: string;
  title: string;
  defaultExpanded: boolean;
  /** Optional — padding-only sections carry just `paddingConfig`; renderers guard for it. */
  fields?: FieldConfig[];
  paddingConfig?: {
    basePath: string;
    label?: string;
  };
}

// ============================================
// Title Configuration (V2 paths)
// ============================================

export const V2_PAYWALL_TITLE_CONFIG: FieldConfig = {
  path: 'content.copy.title',
  label: 'Title',
  placeholder: 'Unlock Premium Features',
  type: 'templateText',
  l10nPath: 'content.copy.title',
  defaultValue: '',
  disableBehavior: 'null',
  guideKey: 'screenedit-paywall-body-title',
  styleFields: remapTextFieldsForV2(createTextStyleFields('content.copy.title', V2_TITLE_TEXT_STYLE_FIELDS)),
  paddingConfig: {
    basePath: 'content.copy.title.layout.insets',
    label: 'Box Padding',
  },
  templateConfig: {
    kindPath: 'content.copy.title.kind',
    labelsPath: 'content.copy.title.templateParams.labels',
    linksPath: 'content.copy.title.templateParams.links',
    countdownsPath: 'content.copy.title.templateParams.countdowns',
  },
};

// ============================================
// Subtitle Configuration (V2 paths)
// ============================================

export const V2_PAYWALL_SUBTITLE_CONFIG: FieldConfig = {
  path: 'content.copy.subtitle',
  label: 'Subtitle',
  placeholder: 'Get unlimited access to all features',
  type: 'templateText',
  l10nPath: 'content.copy.subtitle',
  defaultValue: '',
  disableBehavior: 'null',
  guideKey: 'screenedit-paywall-body-subtitle',
  styleFields: remapTextFieldsForV2(createTextStyleFields('content.copy.subtitle')),
  paddingConfig: {
    basePath: 'content.copy.subtitle.layout.insets',
    label: 'Box Padding',
  },
  templateConfig: {
    kindPath: 'content.copy.subtitle.kind',
    labelsPath: 'content.copy.subtitle.templateParams.labels',
    linksPath: 'content.copy.subtitle.templateParams.links',
    countdownsPath: 'content.copy.subtitle.templateParams.countdowns',
  },
};

// ============================================
// Features (Bullets) Configuration (V2 paths)
// ============================================

export const V2_PAYWALL_FEATURES_ARRAY_CONFIG: FieldConfig = {
  path: 'content.options.items',
  label: 'Features',
  type: 'text',
  defaultValue: [],
  disableBehavior: 'null',
  guideKey: 'screenedit-paywall-body-features',
};

/**
 * V2 Bullet style sections
 * Uses V2 paths: content.options.appearance, content.options.items.0.*
 */
/**
 * Remap V1 text style fields to V2 TypographyStyle:
 *   .styles.* → .appearance.*
 *   .appearance.textAlign → .appearance.align
 *   textAlign options: left/right → start/end
 */
function remapTextFieldsForV2(fields: FieldConfig[]): FieldConfig[] {
  return fields.map(f => {
    let path = f.path.replace('.styles.', '.appearance.');
    const isAlignField = path.endsWith('.appearance.textAlign');
    if (isAlignField) {
      path = path.replace('.appearance.textAlign', '.appearance.align');
      return { ...f, path, options: [...V2_TEXT_ALIGN_OPTIONS] };
    }
    return path !== f.path ? { ...f, path } : f;
  });
}

/**
 * Remap V1 flat border fields to V2 nested BorderStyle paths.
 */
function remapBorderPaths(fields: FieldConfig[]): FieldConfig[] {
  return fields.map(f => {
    let path = f.path;
    if (path.endsWith('.borderWidth')) path = path.replace('.borderWidth', '.borders.width');
    else if (path.endsWith('.borderColor')) path = path.replace('.borderColor', '.borders.color');
    else if (path.endsWith('.borderRadius')) path = path.replace('.borderRadius', '.borders.radius.uniform');
    return path !== f.path ? { ...f, path } : f;
  });
}

export const V2_BULLET_STYLE_SECTIONS: BulletStyleSectionConfig[] = [
  {
    id: 'itemState',
    title: 'Item state',
    defaultExpanded: true,
    fields: remapBorderPaths(createListItemStateFields('content.options.appearance')),
  },
  {
    id: 'itemPadding',
    title: 'Item padding',
    defaultExpanded: false,
    paddingConfig: { basePath: 'content.options.appearance.insets', label: 'Item Padding' },
  },
  {
    id: 'listPadding',
    title: 'List padding',
    defaultExpanded: false,
    paddingConfig: { basePath: 'content.options.layout.insets', label: 'List Padding' },
  },
  {
    id: 'titleStyles',
    title: 'Title styles',
    defaultExpanded: false,
    fields: remapTextFieldsForV2(createTextStyleFields('content.options.items.0.title')),
    paddingConfig: { basePath: 'content.options.items.0.title.layout.insets', label: 'Title Padding' },
  },
  {
    id: 'subtitleStyles',
    title: 'Subtitle styles',
    defaultExpanded: false,
    fields: remapTextFieldsForV2(createTextStyleFields('content.options.items.0.subtitle')),
    paddingConfig: { basePath: 'content.options.items.0.subtitle.layout.insets', label: 'Subtitle Padding' },
  },
  {
    id: 'imageStyles',
    title: 'Image styles',
    defaultExpanded: false,
    fields: createSimpleImageStyleFields('content.options.items.0.image').map(f => ({
      ...f,
      path: f.path.replace('.styles.', '.appearance.'),
    })),
    paddingConfig: { basePath: 'content.options.items.0.image.layout.insets', label: 'Image Padding' },
  },
];

// ============================================
// Factory Functions (V2 structures)
// ============================================

/**
 * Create default V2 feature item (OptionsItem)
 */
export function createDefaultV2FeatureItem(
  locale: string,
  titleText: string = '',
  subtitleText: string = '',
): Record<string, unknown> {
  return {
    title: {
      translations: { [locale]: titleText },
      kind: 'static',
      templateParams: { labels: {}, links: {} },
      appearance: {
        fontSize: 14,
        fontFamily: 'SF Pro',
        fontWeight: 500,
        color: '#333333',
        lineHeight: 20,
        align: 'start',
      },
      layout: { insets: {} },
    },
    subtitle: {
      translations: { [locale]: subtitleText },
      kind: 'static',
      templateParams: { labels: {}, links: {} },
      appearance: {
        fontSize: 12,
        fontFamily: 'SF Pro',
        fontWeight: 400,
        color: '#666666',
        lineHeight: 16,
        align: 'start',
      },
      layout: { insets: {} },
    },
    image: {
      translations: { [locale]: { original: '', small: '' } },
      appearance: {},
      layout: { insets: {} },
    },
  };
}
